// @ts-strict-ignore
import type { ReactNode } from "react";

import type { EmersonProductSearchResult } from "@clinic/emerson/data/splinters/EmersonProductSearchResult.fragment.generated";
import type { EmersonProductSearchResultOpen } from "@clinic/emerson/data/splinters/EmersonProductSearchResultOpen.fragment.generated";
import { useGoToProductDetails } from "@clinic/emerson/hooks/useGoToProductDetails/useGoToProductDetails";
import { useEmersonCatalogClearSearch } from "@emerson/hooks/useEmersonCatalogClearSearch/useEmersonCatalogClearSearch";
import { CatalogSearchBarWithFilters } from "@shared/components/CatalogSearch/CatalogSearchBarWithFilters/CatalogSearchBarWithFilters";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { usePopularProductsAuthenticatedEmersonQuery } from "./PopularProductsAuthenticatedEmerson.query.generated";
import { usePopularProductsUnauthEmersonQuery } from "./PopularProductsUnauthEmerson.query.generated";

interface Props {
  renderResults: (
    searchResults: EmersonProductSearchResult[] | EmersonProductSearchResultOpen[],
    pinnedIds: string[],
    totalCount: number,
    isLoadingNewResults: boolean,
    isLoadingMoreResults: boolean,
    handleClearFilters: () => void,
    handleLoadMore?: () => void
  ) => ReactNode;
  hideFilterTags?: boolean;
  hideSearchField?: boolean;
}

const PopularProducts = ({ renderResults, hideFilterTags, hideSearchField }: Props) => {
  const { isUnauthenticated } = useSharedGlobalConfig();
  const { clearSearchFilters: handleClearFilters } = useEmersonCatalogClearSearch();
  const navigateToPDPForProduct = useGoToProductDetails();

  const { data: unAuthPopularProducts, loading: unAuthPopularProductsLoading } =
    usePopularProductsUnauthEmersonQuery({
      variables: { first: 24, filters: { list: "WHOLESALE_POPULAR_BY_WEEK" }, isUnauthenticated },
      skip: !isUnauthenticated,
    });

  const { data: authPopularProducts, loading: authPopularProductsLoading } =
    usePopularProductsAuthenticatedEmersonQuery({
      variables: { first: 24, filters: { list: "WHOLESALE_POPULAR_BY_WEEK" }, isUnauthenticated },
      skip: isUnauthenticated,
    });

  const data = isUnauthenticated ? unAuthPopularProducts : authPopularProducts;
  const loading = isUnauthenticated ? unAuthPopularProductsLoading : authPopularProductsLoading;
  const { nodes: popularProducts } = (!loading && data?.viewer?.products) || {};

  const goToProductDetails = (productId: string) => {
    return navigateToPDPForProduct(productId);
  };

  return (
    <>
      <CatalogSearchBarWithFilters
        onClearFilters={handleClearFilters}
        goToProductDetails={goToProductDetails}
        hideFilterTags={hideFilterTags}
        hideFilterButton={hideFilterTags}
        hideSearchField={hideSearchField}
        inputSize="normal"
        isMarginless
        isFocused={false}
        entryPointForLogging={SearchApplyEntryPoint.CATALOG_PAGE}
        shouldSyncSearchText
        shouldLogOriginUrl
        eventNameForSearchLogging={
          isUnauthenticated
            ? AnalyticsEvent.EMERSON_UNAUTH_SEARCH_APPLY
            : AnalyticsEvent.EMERSON_SEARCH_APPLY
        }
      />
      {renderResults?.(popularProducts, [], 24, loading, false, handleClearFilters)}
    </>
  );
};

export { PopularProducts, type Props };
